/**
 * Parses "latitude,longitude" strings used for Site.location and Inspector.location.
 * Matches backend parsing in `backend/src/utils/timezone.ts` (getTimezoneByCoordinates).
 */
export function parseLatLngFromLocationString(
  location: string | null | undefined
): { lat: number; lng: number } | null {
  if (location == null || typeof location !== 'string') {
    return null;
  }
  const trimmed = location.trim();
  if (!trimmed) {
    return null;
  }
  const parts = trimmed.split(',').map((p) => p.trim());
  if (parts.length < 2) {
    return null;
  }
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return { lat, lng };
}

const EARTH_RADIUS_KM = 6371;

/**
 * Great-circle distance between two WGS84 points, in kilometres.
 */
export function haversineDistanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}
